// Static ad-content demo data for the VeloRent brand (/ads-content).
//
// VeloRent is a fictional rent-a-car brand used for sales demos. It deliberately
// does NOT trip the shared isDemoBrand() detector (its name has no "demo" and its
// status is ACTIVE — see the VeloRent seed notes), so this page needs its own
// brand check. Everything below is fabricated: no API call is made when the
// selected brand is VeloRent.
//
// Shape mirrors the /api/labs/{google-ads,meta-ads}/ads-live responses:
//   { ads: [...], total: N, accounts: [{ accountId, accountName, count }] }
//
// Ads carrying a `_demoVerdict` are the deliberately broken ones — the page's AI
// consistency check reads that field instead of calling the backend.

const GOOGLE_TOTAL = 47;
const META_TOTAL = 39;

export function isVeloRentBrand(brand) {
  if (!brand) return false;
  const b = typeof brand === 'string' ? { id: brand } : brand;
  if (String(b.id || '').startsWith('9cab92ad')) return true;
  if (/velorent/i.test(b.name || '')) return true;
  if (typeof brand === 'string') {
    try {
      const c = JSON.parse(localStorage.getItem('selectedBrand') || 'null');
      if (c && c.id === brand && /velorent/i.test(c.name || '')) return true;
    } catch (_) { /* ignore */ }
  }
  return false;
}

// Deterministic PRNG — demo numbers must not reshuffle on every render.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function rnd() {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const pick = (rnd, min, max) => min + Math.floor(rnd() * (max - min + 1));
// Rotate a pool so each ad in an ad group gets a different slice of the copy.
const rotate = (arr, offset, count) =>
  Array.from({ length: Math.min(count, arr.length) }, (_, i) => arr[(offset + i) % arr.length]);

const img = (seed, w = 400, h = 300) => `https://picsum.photos/seed/${seed}/${w}/${h}`;

const CONSISTENT = (group) =>
  `Ad copy matches the "${group}" intent — vehicle class, location and offer all line up.`;

/* ------------------------------------------------------------------ *
 * GOOGLE ADS
 * ------------------------------------------------------------------ */

const G_ACCOUNTS = {
  search: { accountId: '412-908-5573', accountName: 'VeloRent TR — Search & PMax' },
  display: { accountId: '778-231-6049', accountName: 'VeloRent TR — Display & Video' },
};

// One entry per ad group. `count` ads are generated from the copy pools; entries
// in `broken` replace the generated ad at that index with a mismatched variant.
const G_GROUPS = [
  {
    account: 'search',
    campaignId: '20144870231',
    campaignName: 'TR | Search | Brand — VeloRent',
    adGroupId: '162880045511',
    adGroupName: 'Brand — Core',
    count: 3,
    url: 'https://www.velorent.com/en',
    path: ['car-rental', 'velorent'],
    keywords: [
      ['velorent', 'EXACT', 18420], ['velorent car rental', 'EXACT', 9140],
      ['velorent istanbul', 'PHRASE', 4310], ['velo rent a car', 'PHRASE', 2870],
      ['velorent booking', 'EXACT', 1960], ['velorent contact', 'BROAD', 0],
    ],
    headlines: [
      'VeloRent — Official Site', 'Book Direct & Save 15%', 'VeloRent Car Rental',
      'Free Cancellation Up to 24h', 'Unlimited Mileage Included', 'Pick Up in 5 Minutes',
      '250+ Cars, 12 Locations', 'No Hidden Fees, Ever', 'Rated 4.8/5 by Drivers',
    ],
    descriptions: [
      'Book directly on velorent.com for the best rate — free cancellation and no card fees.',
      'Full insurance, unlimited mileage and 24/7 roadside assistance on every rental.',
      'Pick up at 12 locations across Türkiye. Skip the counter with online check-in.',
      'Manage or cancel your booking any time from your VeloRent account.',
    ],
    broken: {
      2: {
        adName: 'RSA — Brand Core v4 (competitor angle)',
        headlines: [
          'Cheaper Than Enterprise', 'Compare Rental Prices', 'Best Car Hire Deals',
          'Sixt & Avis Alternatives', 'Low Cost Car Hire', 'Compare 40+ Suppliers',
          'Find A Rental Car Today', 'Prices From 749 TL',
        ],
        descriptions: [
          'Compare car hire prices from dozens of suppliers and book the cheapest option.',
          'See rates from all major rental brands side by side before you decide.',
          'Rental comparison made simple — find a deal in under a minute.',
          'Thousands of cars from suppliers across Türkiye.',
        ],
        url: 'https://www.velorent.com/en/compare',
        verdict: 'inconsistent',
        reason: 'Brand campaign, but the ad copy never mentions VeloRent — it reads as a price-comparison site and names competitor brands (Enterprise, Sixt, Avis) in the headlines, sending brand-intent traffic to a comparison page.',
      },
    },
  },
  {
    account: 'search',
    campaignId: '20144870231',
    campaignName: 'TR | Search | Brand — VeloRent',
    adGroupId: '162880045512',
    adGroupName: 'Brand — Misspellings',
    count: 2,
    url: 'https://www.velorent.com/en',
    path: ['car-rental', 'book'],
    keywords: [
      ['velo rent', 'PHRASE', 3120], ['velorrent', 'BROAD', 880],
      ['velorent com', 'PHRASE', 640], ['velorentt', 'BROAD', 0],
    ],
    headlines: [
      'VeloRent — Official Site', 'Looking For VeloRent?', 'Book Direct & Save 15%',
      'Free Cancellation Up to 24h', 'Unlimited Mileage Included', '250+ Cars, 12 Locations',
      'Instant Online Booking', 'No Hidden Fees, Ever',
    ],
    descriptions: [
      'You found us — book directly on velorent.com for the lowest available rate.',
      'Full insurance and unlimited mileage on every VeloRent rental.',
      'Free cancellation up to 24 hours before pick-up.',
      '24/7 roadside assistance across Türkiye.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144871904',
    campaignName: 'TR | Search | Generic — Car Rental',
    adGroupId: '162880061203',
    adGroupName: 'Car Rental — Generic',
    count: 4,
    url: 'https://www.velorent.com/en/car-rental',
    path: ['car-rental', 'turkey'],
    keywords: [
      ['car rental', 'BROAD', 41200], ['rent a car', 'PHRASE', 33750],
      ['car hire turkey', 'PHRASE', 12480], ['rental car booking', 'EXACT', 7310],
      ['hire a car online', 'BROAD', 4120], ['car rental deals', 'PHRASE', 2640],
      ['auto rental', 'BROAD', 0],
    ],
    headlines: [
      'Rent A Car From 749 TL/Day', 'Car Rental Across Türkiye', 'Free Cancellation',
      'Unlimited Mileage Included', 'Book Online In 60 Seconds', '250+ Cars Ready Now',
      'Full Insurance Included', '12 Pick-Up Locations', 'No Credit Card Fees',
      'Economy To Premium Fleet',
    ],
    descriptions: [
      'Daily rates from 749 TL with unlimited mileage and full insurance included.',
      'Book in under a minute — free cancellation up to 24 hours before pick-up.',
      'Economy, SUV, premium and 7-seater vehicles available at 12 locations.',
      'No hidden fees and no credit card surcharge on any VeloRent booking.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144871904',
    campaignName: 'TR | Search | Generic — Car Rental',
    adGroupId: '162880061204',
    adGroupName: 'Cheap Car Rental',
    count: 3,
    url: 'https://www.velorent.com/en/deals',
    path: ['car-rental', 'deals'],
    keywords: [
      ['cheap car rental', 'PHRASE', 22840], ['budget car hire', 'PHRASE', 8940],
      ['cheapest rent a car', 'EXACT', 5610], ['affordable car rental', 'BROAD', 3180],
      ['low cost car hire', 'PHRASE', 1440],
    ],
    headlines: [
      'Cheap Car Rental From 749 TL', 'Budget Cars, Full Insurance', 'No Hidden Fees',
      'Free Cancellation', 'Unlimited Mileage Included', 'Economy Fleet Available Now',
      'Book Online & Save 15%', 'Best Price Guaranteed',
    ],
    descriptions: [
      'Economy cars from 749 TL/day — insurance and unlimited mileage always included.',
      'Book direct and save 15% versus comparison sites. No card fees.',
      'Free cancellation up to 24 hours before pick-up, on every rate.',
      'Clean, modern economy fleet with an average vehicle age of 14 months.',
    ],
    broken: {
      1: {
        adName: 'RSA — Winter Deal (seasonal)',
        headlines: [
          'Winter Deal — 40% Off', 'Ends 28 February — Book Now', 'Snow Tyres Included',
          'Winter Escape Rates', 'Limited Winter Stock', 'Ski Trip Car Hire',
          'Book Before The Season Ends', 'Winter Campaign 2026',
        ],
        descriptions: [
          'Our winter campaign ends 28 February — lock in 40% off before rates go back up.',
          'All winter bookings include snow tyres and a free additional driver.',
          'Perfect for ski trips to Uludağ and Kartalkaya. Limited winter stock.',
          'Winter campaign terms apply. Offer valid until 28.02.2026.',
        ],
        url: 'https://www.velorent.com/en/campaign/winter-2026',
        verdict: 'inconsistent',
        reason: 'Ad is live in July but promotes a "Winter Deal — ends 28 February" offer with snow tyres and ski-trip messaging, and its final URL points to the expired /campaign/winter-2026 landing page.',
      },
    },
  },
  {
    account: 'search',
    campaignId: '20144871904',
    campaignName: 'TR | Search | Generic — Car Rental',
    adGroupId: '162880061205',
    adGroupName: 'Rent A Car — Daily',
    count: 3,
    url: 'https://www.velorent.com/en/daily-rental',
    path: ['car-rental', 'daily'],
    keywords: [
      ['daily car rental', 'PHRASE', 14320], ['rent a car for a day', 'PHRASE', 6870],
      ['one day car hire', 'EXACT', 3940], ['24 hour car rental', 'BROAD', 1720],
    ],
    headlines: [
      'Daily Car Rental From 749 TL', 'Rent For Just One Day', '24-Hour Rentals',
      'Free Cancellation', 'Unlimited Mileage', 'Same-Day Pick Up Available',
      'Full Insurance Included', 'Book Online In 60 Seconds',
    ],
    descriptions: [
      'Need a car for a single day? Daily rates start at 749 TL, all-inclusive.',
      'Same-day pick up at 12 locations — reserve online and collect in minutes.',
      'Unlimited mileage and full insurance on every daily rental.',
      'Free cancellation up to 24 hours before pick-up.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144873580',
    campaignName: 'TR | Search | Airports',
    adGroupId: '162880077814',
    adGroupName: 'Istanbul Airport (IST)',
    count: 4,
    url: 'https://www.velorent.com/en/locations/istanbul-airport',
    path: ['airport', 'istanbul'],
    keywords: [
      ['istanbul airport car rental', 'EXACT', 26310], ['ist airport rent a car', 'PHRASE', 11450],
      ['car hire istanbul airport', 'PHRASE', 8720], ['rent a car ist', 'BROAD', 4180],
      ['istanbul havalimanı araç kiralama', 'PHRASE', 15640], ['airport car rental istanbul', 'BROAD', 2210],
    ],
    headlines: [
      'Istanbul Airport Car Rental', 'Desk In The Arrivals Hall', 'Meet & Greet On Arrival',
      'Free Cancellation', 'Unlimited Mileage Included', 'Open 24/7 At IST',
      'Skip The Queue — Online Check-In', 'From 899 TL/Day At IST', 'Flight Delay? We Wait.',
    ],
    descriptions: [
      'Our desk is in the IST arrivals hall — collect your keys minutes after landing.',
      'We track your flight, so a delay never costs you your reservation.',
      'Open 24/7 at Istanbul Airport with unlimited mileage and full insurance.',
      'Online check-in lets you skip the counter queue entirely.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144873580',
    campaignName: 'TR | Search | Airports',
    adGroupId: '162880077815',
    adGroupName: 'Sabiha Gökçen (SAW)',
    count: 3,
    url: 'https://www.velorent.com/en/locations/sabiha-gokcen',
    path: ['airport', 'sabiha-gokcen'],
    keywords: [
      ['sabiha gokcen car rental', 'EXACT', 12870], ['saw airport rent a car', 'PHRASE', 6340],
      ['sabiha gökçen araç kiralama', 'PHRASE', 9210], ['car hire saw', 'BROAD', 1180],
    ],
    headlines: [
      'Sabiha Gökçen Car Rental', 'Desk At SAW Arrivals', 'Open 24/7 At SAW',
      'Free Cancellation', 'Unlimited Mileage Included', 'From 849 TL/Day At SAW',
      'Flight Tracking Included', 'Online Check-In Available',
    ],
    descriptions: [
      'Collect your car at the Sabiha Gökçen arrivals hall, open 24 hours a day.',
      'We monitor your flight — late landings never cost you your booking.',
      'Unlimited mileage and full insurance on every SAW rental.',
      'Free cancellation up to 24 hours before pick-up.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144873580',
    campaignName: 'TR | Search | Airports',
    adGroupId: '162880077816',
    adGroupName: 'Antalya Airport (AYT)',
    count: 3,
    url: 'https://www.velorent.com/en/locations/antalya-airport',
    path: ['airport', 'antalya'],
    keywords: [
      ['antalya airport car rental', 'EXACT', 19640], ['ayt rent a car', 'PHRASE', 7280],
      ['antalya havalimanı araç kiralama', 'PHRASE', 13910], ['car hire antalya', 'BROAD', 5120],
      ['antalya airport transfer car', 'BROAD', 0],
    ],
    headlines: [
      'Antalya Airport Car Rental', 'Desk At AYT Terminal 1', 'Open 24/7 At Antalya',
      'Free Cancellation', 'Unlimited Mileage Included', 'From 799 TL/Day At AYT',
      'Convertibles & SUVs Available', 'Flight Tracking Included',
    ],
    descriptions: [
      'Pick up at Antalya Airport Terminal 1 — the desk is open around the clock.',
      'Convertibles, SUVs and 7-seaters ready for the coast road to Kaş and Side.',
      'Unlimited mileage and full insurance on every Antalya rental.',
      'Free cancellation up to 24 hours before pick-up.',
    ],
    broken: {
      0: {
        adName: 'RSA — AYT v2 (IST copy)',
        headlines: [
          'Istanbul Airport Car Rental', 'Desk In IST Arrivals Hall', 'Rent A Car At IST',
          'Free Shuttle To Our IST Lot', 'Open 24/7 At Istanbul Airport', 'From 899 TL/Day At IST',
          'Closest Desk To IST Arrivals', 'Serving Istanbul Since 2016',
        ],
        descriptions: [
          'Our Istanbul Airport desk is in the arrivals hall — keys within minutes of landing.',
          'Free shuttle from IST arrivals to the VeloRent lot every 10 minutes.',
          'Serving Istanbul Airport travellers 24 hours a day, 7 days a week.',
          'Book your Istanbul Airport rental online and skip the counter.',
        ],
        url: 'https://www.velorent.com/en/locations/istanbul-airport',
        verdict: 'inconsistent',
        reason: 'Ad group targets Antalya Airport (AYT) keywords, but every headline and the final URL promote the Istanbul Airport (IST) location — searchers looking for Antalya land on an IST page 700 km away.',
      },
    },
  },
  {
    account: 'search',
    campaignId: '20144873580',
    campaignName: 'TR | Search | Airports',
    adGroupId: '162880077817',
    adGroupName: 'Izmir Airport (ADB)',
    count: 2,
    url: 'https://www.velorent.com/en/locations/izmir-airport',
    path: ['airport', 'izmir'],
    keywords: [
      ['izmir airport car rental', 'EXACT', 8410], ['adb rent a car', 'PHRASE', 3260],
      ['izmir havalimanı araç kiralama', 'PHRASE', 6180],
    ],
    headlines: [
      'Izmir Airport Car Rental', 'Desk At ADB Arrivals', 'Open 24/7 At Izmir',
      'Free Cancellation', 'Unlimited Mileage Included', 'From 779 TL/Day At ADB',
      'Ready For Çeşme & Alaçatı', 'Online Check-In Available',
    ],
    descriptions: [
      'Collect at Adnan Menderes Airport arrivals — open 24 hours a day.',
      'The easiest way to reach Çeşme, Alaçatı and the Aegean coast.',
      'Unlimited mileage and full insurance on every Izmir rental.',
      'Free cancellation up to 24 hours before pick-up.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144876112',
    campaignName: 'TR | Search | Long Term Rental',
    adGroupId: '162880094327',
    adGroupName: 'Monthly Rental',
    count: 3,
    url: 'https://www.velorent.com/en/long-term',
    path: ['long-term', 'monthly'],
    keywords: [
      ['monthly car rental', 'PHRASE', 16720], ['long term car hire', 'PHRASE', 9480],
      ['rent a car monthly', 'EXACT', 6140], ['6 month car rental', 'BROAD', 2870],
      ['uzun dönem araç kiralama', 'PHRASE', 11260],
    ],
    headlines: [
      'Monthly Car Rental From 18.900 TL', 'Long-Term Rental, No Deposit', '1 To 24 Month Terms',
      'Maintenance & Insurance Included', 'Swap Your Car Any Time', 'Free Delivery To Your Door',
      'Cheaper Than Leasing', 'No Down Payment Required',
    ],
    descriptions: [
      'Monthly rentals from 18.900 TL — maintenance, insurance and tyres all included.',
      'Terms from 1 to 24 months with no deposit and no down payment.',
      'Free delivery to your home or office anywhere in Istanbul, Ankara and Izmir.',
      'Change your vehicle class at any point during the contract.',
    ],
    broken: {
      0: {
        adName: 'RSA — Long Term v3 (daily offer)',
        headlines: [
          'Rent A Car From 749 TL/Day', 'Weekend Rates — 3 Day Minimum', 'Book For 1-3 Days',
          'Daily Car Hire Deals', 'Pick Up Today, Return Tomorrow', 'Short Trip? Rent Daily.',
          'Hourly & Daily Options', '24-Hour Rentals Available',
        ],
        descriptions: [
          'Daily rentals from 749 TL — perfect for a weekend away or a quick city trip.',
          'Minimum rental period is 3 days. Return any time within 72 hours.',
          'Same-day pick up available at all 12 locations.',
          'Ideal for short trips — book online and collect in minutes.',
        ],
        url: 'https://www.velorent.com/en/daily-rental',
        verdict: 'inconsistent',
        reason: 'Campaign and ad group sell long-term monthly rentals, but the copy advertises daily/weekend rates with a 3-day minimum and points to the daily-rental page — the exact opposite of the searcher\'s intent.',
      },
    },
  },
  {
    account: 'search',
    campaignId: '20144876112',
    campaignName: 'TR | Search | Long Term Rental',
    adGroupId: '162880094328',
    adGroupName: 'Corporate Fleet Leasing',
    count: 2,
    url: 'https://www.velorent.com/en/business',
    path: ['business', 'fleet'],
    keywords: [
      ['corporate car rental', 'PHRASE', 7410], ['fleet leasing turkey', 'PHRASE', 3820],
      ['business car hire', 'EXACT', 2560], ['kurumsal araç kiralama', 'PHRASE', 9140],
    ],
    headlines: [
      'Corporate Fleet Rental', 'From 5 To 500 Vehicles', 'Dedicated Account Manager',
      'Monthly Invoicing, One Bill', 'Maintenance Fully Managed', 'Replacement Car Guaranteed',
      'Custom Fleet Reporting', 'Request A Quote Today',
    ],
    descriptions: [
      'Fleets from 5 to 500 vehicles with a dedicated account manager and one monthly invoice.',
      'Maintenance, insurance and replacement vehicles fully managed by VeloRent.',
      'Live fleet reporting and cost breakdowns in your VeloRent Business dashboard.',
      'Request a tailored quote — most proposals return within one business day.',
    ],
  },
  {
    account: 'search',
    campaignId: '20144878445',
    campaignName: 'TR | Search | Electric Vehicles',
    adGroupId: '162880110932',
    adGroupName: 'Electric Car Rental',
    count: 3,
    url: 'https://www.velorent.com/en/electric',
    path: ['electric', 'ev-rental'],
    keywords: [
      ['electric car rental', 'PHRASE', 11840], ['rent an electric car', 'EXACT', 5270],
      ['ev car hire turkey', 'PHRASE', 2910], ['elektrikli araç kiralama', 'PHRASE', 14380],
      ['tesla rental istanbul', 'BROAD', 4120], ['hybrid car rental', 'BROAD', 0],
    ],
    headlines: [
      'Electric Car Rental From 1.149 TL', 'Zero Emission Fleet', 'Free Charging Card Included',
      '400+ km Range Models', 'Silent, Smooth, Emission-Free', 'Charging Map In The App',
      'EV Fleet At 6 Locations', 'Try An EV For A Weekend',
    ],
    descriptions: [
      'Rent a fully electric car from 1.149 TL/day, charging card included at no extra cost.',
      'Models with 400+ km of real-world range, ready at six VeloRent locations.',
      'Our app shows every compatible charger on your route across Türkiye.',
      'Zero emissions, zero fuel stops — free cancellation up to 24 hours before pick-up.',
    ],
    broken: {
      1: {
        adName: 'RSA — EV v2 (economy diesel copy)',
        headlines: [
          'Economy Diesel From 749 TL', 'Best Fuel Economy — 4.2 L/100km', 'Diesel Sedans Available',
          'Cheapest Cars In Our Fleet', 'Fill Up And Go', 'Manual & Automatic Diesel',
          'Long Range On One Tank', 'Budget Economy Sedans',
        ],
        descriptions: [
          'Our diesel economy sedans return 4.2 L/100km — the cheapest way to cover distance.',
          'Fill up at any station in Türkiye and go. No planning, no charging stops.',
          'Economy diesel hatchbacks and sedans from 749 TL/day.',
          'Manual and automatic diesel options available at all 12 locations.',
        ],
        url: 'https://www.velorent.com/en/car-rental',
        verdict: 'inconsistent',
        reason: 'Ad group targets electric-vehicle searches (including "elektrikli araç kiralama" and "tesla rental istanbul"), yet the copy sells diesel economy sedans, boasts fuel consumption and links to the generic fleet page — it actively contradicts the search intent.',
      },
    },
  },
  {
    account: 'search',
    campaignId: '20144880067',
    campaignName: 'TR | Search | Competitor',
    adGroupId: '162880127441',
    adGroupName: 'Competitor — Brands',
    count: 2,
    status: 'PAUSED',
    url: 'https://www.velorent.com/en/why-velorent',
    path: ['compare', 'velorent'],
    keywords: [
      ['car rental alternatives', 'PHRASE', 3140], ['best car rental turkey', 'PHRASE', 5820],
      ['compare car hire turkey', 'BROAD', 1960],
    ],
    headlines: [
      'Try VeloRent Instead', 'Rated 4.8/5 By 26.000 Drivers', 'No Hidden Fees, Ever',
      'Free Cancellation', 'Unlimited Mileage Included', 'Newer Fleet, Lower Price',
      'Book Direct & Save 15%', '12 Locations Across Türkiye',
    ],
    descriptions: [
      'See why 26.000 drivers rated VeloRent 4.8/5 — no hidden fees and no card surcharge.',
      'Average vehicle age of 14 months, unlimited mileage on every rate.',
      'Free cancellation up to 24 hours before pick-up, on every booking.',
      'Book direct on velorent.com and save 15% versus comparison sites.',
    ],
  },
  {
    account: 'display',
    campaignId: '20144882913',
    campaignName: 'TR | Display | Remarketing',
    adGroupId: '162880143008',
    adGroupName: 'Remarketing — Site Visitors 30d',
    count: 4,
    adType: 'RESPONSIVE_DISPLAY_AD',
    url: 'https://www.velorent.com/en/car-rental',
    businessName: 'VeloRent',
    headlines: [
      'Still Looking For A Car?', 'Your Dates Are Still Available', 'Free Cancellation',
      'From 749 TL/Day', 'Unlimited Mileage Included', 'Finish Your Booking',
    ],
    descriptions: [
      'The car you were looking at is still available for your dates. Book in 60 seconds.',
      'Free cancellation up to 24 hours before pick-up — reserve now, decide later.',
      'Unlimited mileage and full insurance on every VeloRent rental.',
    ],
    longHeadlines: [
      'Your VeloRent booking is one click away — free cancellation included',
      'Still available for your dates: 250+ cars from 749 TL/day',
      'Reserve now, cancel free up to 24 hours before pick-up',
    ],
  },
  {
    account: 'display',
    campaignId: '20144882913',
    campaignName: 'TR | Display | Remarketing',
    adGroupId: '162880143009',
    adGroupName: 'Remarketing — Cart Abandoners',
    count: 3,
    adType: 'RESPONSIVE_DISPLAY_AD',
    url: 'https://www.velorent.com/en/checkout/resume',
    businessName: 'VeloRent',
    headlines: [
      'Your Booking Is Waiting', 'Complete In 30 Seconds', 'We Held Your Car',
      'Free Cancellation', 'Finish & Save 15%', 'Your Rate Is Locked For 48h',
    ],
    descriptions: [
      'We held your car and your rate for 48 hours — finish the booking in 30 seconds.',
      'Complete your reservation now and keep the 15% direct-booking discount.',
      'Free cancellation up to 24 hours before pick-up.',
    ],
    longHeadlines: [
      'Your car is still on hold — complete your VeloRent booking now',
      'We locked your rate for 48 hours. Finish in 30 seconds.',
      'One step left: confirm your VeloRent reservation',
    ],
  },
  {
    account: 'display',
    campaignId: '20144885236',
    campaignName: 'TR | Video | Brand Awareness',
    adGroupId: '162880159117',
    adGroupName: 'Brand Awareness — TR',
    count: 3,
    adType: 'VIDEO_RESPONSIVE_AD',
    url: 'https://www.velorent.com/en',
    businessName: 'VeloRent',
    headlines: [
      'Drive Türkiye, Your Way', 'VeloRent — 12 Locations', 'The Road Is Yours',
      'Book In 60 Seconds', 'No Hidden Fees, Ever', '250+ Cars Ready Now',
    ],
    descriptions: [
      'From Istanbul to the Aegean coast — VeloRent gets you on the road in minutes.',
      'Unlimited mileage, full insurance and free cancellation on every rental.',
      '12 locations, 250+ cars, one simple booking flow.',
    ],
    longHeadlines: [
      'Drive Türkiye your way — VeloRent, 12 locations and 250+ cars',
      'The road is yours: unlimited mileage on every VeloRent rental',
      'Book a car in 60 seconds and get on the road',
    ],
  },
];

export function buildVeloRentGoogleAds() {
  const rnd = mulberry32(0x5E10);
  const ads = [];

  G_GROUPS.forEach((g, gi) => {
    const acc = G_ACCOUNTS[g.account];
    const adType = g.adType || 'RESPONSIVE_SEARCH_AD';
    const isSearch = adType === 'RESPONSIVE_SEARCH_AD';

    for (let i = 0; i < g.count; i++) {
      const broken = g.broken?.[i];
      // Paused ads still show impressions from before they were switched off.
      const paused = g.status === 'PAUSED' || (!broken && gi % 5 === 2 && i === g.count - 1);
      const impr30 = pick(rnd, 4200, 96000);
      const impr7 = Math.round(impr30 * (0.16 + rnd() * 0.09));
      const imprY = Math.max(12, Math.round(impr7 / (6 + rnd() * 2)));
      const clicks = Math.round(impr30 * (0.032 + rnd() * 0.048));
      const cost = Math.round(clicks * (7.4 + rnd() * 9.2));
      const adNum = ads.length + 1;

      const headlines = broken?.headlines || rotate(g.headlines, i * 2, isSearch ? 8 : 5);
      const descriptions = broken?.descriptions || rotate(g.descriptions, i, isSearch ? 4 : 3);
      const longHeadline = g.longHeadlines ? g.longHeadlines[i % g.longHeadlines.length] : '';
      const images = isSearch ? [] : [
        img(`velorent-g${adNum}a`), img(`velorent-g${adNum}b`), img(`velorent-g${adNum}c`),
      ];

      const ad = {
        accountId: acc.accountId,
        accountName: acc.accountName,
        campaignId: g.campaignId,
        campaignName: g.campaignName,
        campaignStatus: 'ENABLED',
        adGroupId: g.adGroupId,
        adGroupName: g.adGroupName,
        adGroupStatus: g.status === 'PAUSED' ? 'PAUSED' : 'ENABLED',
        adId: String(710000000000 + adNum * 137),
        adName: broken?.adName || `${isSearch ? 'RSA' : adType === 'RESPONSIVE_DISPLAY_AD' ? 'RDA' : 'Video'} — ${g.adGroupName} v${i + 1}`,
        adType,
        status: paused ? 'PAUSED' : 'ENABLED',
        keywords: (g.keywords || []).map(([text, matchType, impressions30d]) => ({
          text, matchType, impressions30d,
        })),
        headlines,
        descriptions,
        longHeadline,
        path1: g.path?.[0] || '',
        path2: g.path?.[1] || '',
        businessName: g.businessName || '',
        phoneNumber: isSearch ? '+90 850 460 0 460' : '',
        finalUrls: [broken?.url || g.url],
        images,
        rawTexts: [...headlines, ...descriptions, longHeadline].filter(Boolean),
        cost30d: cost,
        impressions30d: impr30,
        impressions7d: impr7,
        impressionsYesterday: imprY,
        impressionsToday: 0, // Google reporting is delayed — there is no "today".
        clicks30d: clicks,
      };
      if (broken) {
        ad._demoVerdict = { verdict: broken.verdict, reason: broken.reason };
      }
      ads.push(ad);
    }
  });

  const accounts = Object.values(G_ACCOUNTS).map((a) => ({
    accountId: a.accountId,
    accountName: a.accountName,
    count: ads.filter((x) => x.accountId === a.accountId).length,
  }));

  return { ads, total: ads.length, accounts };
}

/* ------------------------------------------------------------------ *
 * META ADS
 * ------------------------------------------------------------------ */

const M_ACCOUNTS = {
  main: { accountId: 'act_1044820937', accountName: 'VeloRent TR — Meta Business' },
  catalog: { accountId: 'act_1044829164', accountName: 'VeloRent TR — Fleet Catalog' },
};

const M_GROUPS = [
  {
    account: 'main',
    campaignId: '23859114402710488',
    campaignName: 'TR | Conversions | Summer Road Trip',
    adsetId: '23859114402730488',
    adsetName: 'Broad 25-54 | TR',
    count: 4,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/summer',
    titles: [
      'Summer Road Trip From 749 TL/Day', 'Your Summer Car Is Ready', 'Unlimited Mileage, All Summer',
      'Coast Roads Are Calling', 'Free Cancellation On Every Rate',
    ],
    bodies: [
      'Cabrios, SUVs and 7-seaters ready at 12 locations. Unlimited mileage, full insurance, free cancellation up to 24 hours before pick-up. From 749 TL/day.',
      'From Istanbul to Kaş in one booking. Pick up in minutes, drive as far as you like — mileage is always unlimited on VeloRent.',
      'Book your summer car before the coast fills up. 250+ vehicles, 12 pick-up points, no hidden fees and no card surcharge.',
      'Planning the Aegean loop? Reserve now, pay at pick-up, cancel free if plans change. Full insurance included on every rate.',
    ],
    descriptions: ['Unlimited mileage · Full insurance · Free cancellation'],
    broken: {
      2: {
        adName: 'Summer — Static 03 (price test)',
        title: 'Summer Cars From 749 TL/Day',
        body: 'Our summer fleet starts at 1.199 TL/day with unlimited mileage and full insurance. Book online and pick up at any of our 12 locations across Türkiye.',
        description: 'From 899 TL/day · Terms apply',
        verdict: 'inconsistent',
        reason: 'The same ad quotes three different starting prices — the title says 749 TL/day, the body says 1.199 TL/day and the link description says 899 TL/day, so no rate on the creative can be trusted.',
      },
    },
  },
  {
    account: 'main',
    campaignId: '23859114402710488',
    campaignName: 'TR | Conversions | Summer Road Trip',
    adsetId: '23859114402730489',
    adsetName: 'Interests — Travel & Car Rental',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/summer',
    titles: [
      'Rent A Car For The Coast', 'Summer Fleet Now Available', 'Drive The Aegean This Summer',
      'Book Now, Cancel Free',
    ],
    bodies: [
      'Convertibles and SUVs ready in Antalya, Izmir and Bodrum. Unlimited mileage, full insurance, free cancellation. From 749 TL/day.',
      'Skip the transfers — pick up your car on arrival and set your own route along the coast.',
      'Travelling this summer? Reserve a car now and cancel free up to 24 hours before pick-up.',
    ],
    descriptions: ['12 locations across Türkiye'],
  },
  {
    account: 'main',
    campaignId: '23859114409870512',
    campaignName: 'TR | Conversions | Airport Transfers',
    adsetId: '23859114409890512',
    adsetName: 'IST + SAW Radius',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/locations/istanbul-airport',
    titles: [
      'Land, Collect, Drive', 'Car Waiting At IST Arrivals', 'Open 24/7 At IST & SAW',
      'Flight Delayed? We Wait.',
    ],
    bodies: [
      'Our desks are inside the IST and SAW arrivals halls. Online check-in means keys in your hand minutes after you land — we track your flight, so delays never cost you the booking.',
      'No queue, no shuttle, no waiting. Reserve online and collect at arrivals, 24 hours a day.',
      'From 899 TL/day at Istanbul Airport with unlimited mileage and full insurance included.',
    ],
    descriptions: ['Desk in the arrivals hall · Open 24/7'],
  },
  {
    account: 'main',
    campaignId: '23859114409870512',
    campaignName: 'TR | Conversions | Airport Transfers',
    adsetId: '23859114409890513',
    adsetName: 'Antalya Radius',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/locations/antalya-airport',
    titles: [
      'Antalya Airport Pick-Up', 'Your Car Is At AYT Terminal 1', 'Coast Ready From 799 TL/Day',
      'Open 24/7 At Antalya',
    ],
    bodies: [
      'Collect at Antalya Airport Terminal 1 and be on the coast road in 15 minutes. Convertibles, SUVs and 7-seaters available.',
      'Unlimited mileage on every Antalya rental — drive to Side, Kaş or Kalkan without a second thought.',
      'Free cancellation up to 24 hours before pick-up. Full insurance always included.',
    ],
    descriptions: ['Terminal 1 desk · Open 24/7'],
  },
  {
    account: 'main',
    campaignId: '23859114417220633',
    campaignName: 'TR | Retargeting | Booking Funnel',
    adsetId: '23859114417240633',
    adsetName: 'Retargeting — Cart Abandoners',
    count: 4,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/checkout/resume',
    titles: [
      'Your Booking Is Still Open', 'We Held Your Car For 48h', 'One Step Left',
      'Finish & Keep Your 15% Off',
    ],
    bodies: [
      'You were one step away. We held your car and your rate for 48 hours — finish the booking in 30 seconds and keep the 15% direct discount.',
      'Your dates are still available. Complete your reservation now, cancel free up to 24 hours before pick-up.',
      'Still deciding? Your rate is locked until tomorrow. No payment needed until pick-up.',
      'Your car is on hold. Confirm now and we will have it ready when you arrive.',
    ],
    descriptions: ['Rate locked for 48 hours'],
    broken: {
      0: {
        adName: 'RT Cart — Brand Story 01',
        title: 'Who Is VeloRent?',
        body: 'Founded in 2016 in Izmir, VeloRent grew from 8 cars to a fleet of 250+ across 12 locations. We believe renting a car should be simple, transparent and fair. Get to know our story and the team behind the wheel.',
        description: 'Our story · Since 2016',
        cta: 'LEARN_MORE',
        url: 'https://www.velorent.com/en/about-us',
        verdict: 'inconsistent',
        reason: 'The ad set retargets users who abandoned a booking at checkout, but the creative is a cold-audience brand story with a "Learn More" button pointing at the About Us page — it never mentions the pending reservation or offers a way back to it.',
      },
    },
  },
  {
    account: 'main',
    campaignId: '23859114417220633',
    campaignName: 'TR | Retargeting | Booking Funnel',
    adsetId: '23859114417240634',
    adsetName: 'Retargeting — Site Visitors 30d',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/car-rental',
    titles: [
      'Still Looking For A Car?', 'Your Dates Are Available', 'From 749 TL/Day',
      'Free Cancellation, Always',
    ],
    bodies: [
      'The car you viewed is still available for your dates. Book in 60 seconds with free cancellation.',
      'Unlimited mileage, full insurance, no card fees. Reserve now and decide later.',
      '250+ cars across 12 locations — find yours and book direct to save 15%.',
    ],
    descriptions: ['Book direct and save 15%'],
  },
  {
    account: 'catalog',
    campaignId: '23859114425510877',
    campaignName: 'TR | Catalog | Fleet DPA',
    adsetId: '23859114425530877',
    adsetName: 'DPA — Broad Fleet',
    count: 4,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/fleet',
    dpa: true,
    titles: [
      '{{product.name}} From {{product.price}}', 'Available At 12 Locations', 'Your Class, Your Price',
      'Book The Exact Car You Want',
    ],
    bodies: [
      'Pick the exact vehicle class you want — guaranteed model group, not "or similar". Unlimited mileage and full insurance on every rate.',
      'From economy hatchbacks to 7-seaters and electric compacts. 250+ cars, 12 pick-up points.',
      'See live availability and prices for your dates. Free cancellation up to 24 hours before pick-up.',
      'Guaranteed model group on every booking — no surprises at the counter.',
    ],
    descriptions: ['Guaranteed model group · Unlimited mileage'],
    broken: {
      1: {
        adName: 'DPA — Fleet 02 (app CTA)',
        title: 'Download The VeloRent App',
        body: 'Get 20% off your first booking when you reserve through the VeloRent app. Manage your rental, unlock your car and extend your booking straight from your phone.',
        description: 'Available on iOS and Android',
        cta: 'DOWNLOAD',
        url: 'https://www.velorent.com/en/fleet',
        verdict: 'inconsistent',
        reason: 'Copy and call-to-action push an app download ("Download", iOS/Android, app-only 20% discount) but the destination link opens the web fleet page — VeloRent has no published mobile app, so the promised offer cannot be redeemed.',
      },
    },
  },
  {
    account: 'catalog',
    campaignId: '23859114425510877',
    campaignName: 'TR | Catalog | Fleet DPA',
    adsetId: '23859114425530878',
    adsetName: 'DPA — Retargeting',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/fleet',
    dpa: true,
    titles: [
      'Still Available: {{product.name}}', 'The Car You Viewed', 'Your Class, Still Free',
      'Book Before It Goes',
    ],
    bodies: [
      'The vehicle class you looked at is still available for your dates — but stock moves fast in season.',
      'Reserve the exact model group you viewed. Free cancellation up to 24 hours before pick-up.',
      'Unlimited mileage and full insurance included. Book direct and save 15%.',
    ],
    descriptions: ['Still available for your dates'],
  },
  {
    account: 'main',
    campaignId: '23859114433910204',
    campaignName: 'TR | Awareness | Brand Video',
    adsetId: '23859114433930204',
    adsetName: 'Video Views — TR',
    count: 3,
    cta: 'LEARN_MORE',
    url: 'https://www.velorent.com/en',
    titles: [
      'Drive Türkiye, Your Way', 'VeloRent — 12 Locations', 'The Road Is Yours',
    ],
    bodies: [
      'From the Bosphorus to the Aegean coast — 250+ cars, 12 locations and one simple booking flow.',
      'No hidden fees, no card surcharge, no "or similar". Just the car you booked, ready when you land.',
      'Unlimited mileage on every rental, because the point of a car is to drive it.',
    ],
    descriptions: ['250+ cars · 12 locations'],
    broken: {
      1: {
        adName: 'Brand Video — NY Deal 02',
        title: 'New Year Deal — 45% Off',
        body: 'Start the year on the road. Book before 5 January and get 45% off any rental collected in January. Snow tyres included on all winter bookings — limited New Year stock.',
        description: 'Offer ends 5 January',
        verdict: 'inconsistent',
        reason: 'A New Year campaign creative ("book before 5 January", "45% off January rentals", snow tyres) is still delivering in July — the advertised deadline passed roughly six months ago and the discount no longer exists.',
      },
    },
  },
  {
    account: 'main',
    campaignId: '23859114441180955',
    campaignName: 'TR | Conversions | Long Term Rental',
    adsetId: '23859114441200955',
    adsetName: 'Corporate / B2B',
    count: 3,
    cta: 'GET_QUOTE',
    url: 'https://www.velorent.com/en/business',
    titles: [
      'Fleets From 5 To 500 Cars', 'One Invoice, Zero Admin', 'Corporate Fleet Rental',
      'Request A Fleet Quote',
    ],
    bodies: [
      'Dedicated account manager, one monthly invoice and fully managed maintenance. Replacement vehicle guaranteed within 4 hours.',
      'Live fleet reporting and cost breakdowns in the VeloRent Business dashboard.',
      'Most fleet proposals return within one business day. Tell us your headcount and routes.',
    ],
    descriptions: ['Dedicated account manager'],
    broken: {
      1: {
        adName: 'B2B — Weekend Cabrio 02',
        title: 'Weekend Escape In A Convertible',
        body: 'Two seats, roof down, coast road ahead. Grab a convertible for the weekend from 1.899 TL/day and make Friday afternoon count. Perfect for couples.',
        description: 'Two-seater · Weekend rates',
        cta: 'BOOK_TRAVEL',
        url: 'https://www.velorent.com/en/summer',
        verdict: 'inconsistent',
        reason: 'The ad set targets corporate fleet decision makers with a quote-request objective, but the creative sells a two-seater convertible for a romantic weekend and links to the consumer summer page — wrong audience, wrong vehicle and wrong conversion path.',
      },
    },
  },
  {
    account: 'main',
    campaignId: '23859114441180955',
    campaignName: 'TR | Conversions | Long Term Rental',
    adsetId: '23859114441200956',
    adsetName: 'Monthly Rental — Broad',
    count: 3,
    cta: 'LEARN_MORE',
    url: 'https://www.velorent.com/en/long-term',
    titles: [
      'Monthly Rental From 18.900 TL', 'No Deposit, No Down Payment', '1 To 24 Month Terms',
      'Cheaper Than Leasing',
    ],
    bodies: [
      'Maintenance, insurance and tyres included. Swap your vehicle class any time during the contract.',
      'Free delivery to your home or office in Istanbul, Ankara and Izmir.',
      'From 18.900 TL/month with no deposit and no down payment required.',
    ],
    descriptions: ['Maintenance & insurance included'],
  },
  {
    account: 'main',
    campaignId: '23859114448760331',
    campaignName: 'TR | Conversions | Electric Fleet',
    adsetId: '23859114448780331',
    adsetName: 'EV Interest',
    count: 3,
    cta: 'BOOK_TRAVEL',
    url: 'https://www.velorent.com/en/electric',
    titles: [
      'Electric From 1.149 TL/Day', 'Charging Card Included', '400+ km Real Range',
      'Try An EV This Weekend',
    ],
    bodies: [
      'Rent a fully electric car with a charging card included at no extra cost. Our app maps every compatible charger on your route.',
      'Models with 400+ km of real-world range, available at six VeloRent locations.',
      'Silent, smooth and emission-free — free cancellation up to 24 hours before pick-up.',
    ],
    descriptions: ['Charging card included'],
  },
];

export function buildVeloRentMetaAds() {
  const rnd = mulberry32(0x4D3A);
  const ads = [];

  M_GROUPS.forEach((g) => {
    const acc = M_ACCOUNTS[g.account];

    for (let i = 0; i < g.count; i++) {
      const broken = g.broken?.[i];
      const impr30 = pick(rnd, 18000, 410000);
      const impr7 = Math.round(impr30 * (0.15 + rnd() * 0.1));
      const imprY = Math.max(40, Math.round(impr7 / (6.5 + rnd() * 1.5)));
      const imprT = Math.round(imprY * (0.28 + rnd() * 0.5));
      const spend = Math.round(impr30 * (0.011 + rnd() * 0.019) * 100) / 100;
      const adNum = ads.length + 1;

      const title = broken?.title || g.titles[i % g.titles.length];
      const body = broken?.body || g.bodies[i % g.bodies.length];
      const description = broken?.description || g.descriptions[0];
      const titles = broken ? [title] : rotate(g.titles, i, Math.min(3, g.titles.length));
      const bodies = broken ? [body] : rotate(g.bodies, i, Math.min(3, g.bodies.length));

      const ad = {
        accountId: acc.accountId,
        accountName: acc.accountName,
        campaignId: g.campaignId,
        campaignName: g.campaignName,
        adsetId: g.adsetId,
        adsetName: g.adsetName,
        adId: String(23859114500000000 + adNum * 4391),
        adName: broken?.adName || `${g.adsetName} — Creative ${String(i + 1).padStart(2, '0')}`,
        status: 'ACTIVE',
        effectiveStatus: 'ACTIVE',
        creativeId: String(23859114600000000 + adNum * 7717),
        creativeName: `${g.adsetName} / ${broken ? 'variant' : 'base'} ${i + 1}`,
        title,
        body,
        description,
        titles,
        bodies,
        descriptions: [description],
        linkUrl: broken?.url || g.url,
        callToAction: broken?.cta || g.cta,
        imageUrl: img(`velorent-m${adNum}`, 600, 600),
        thumbnailUrl: img(`velorent-m${adNum}`, 160, 160),
        images: [img(`velorent-m${adNum}`, 600, 600), img(`velorent-m${adNum}b`, 600, 600)],
        rawTexts: [...titles, ...bodies, description].filter(Boolean),
        spend30d: spend,
        impressions30d: impr30,
        impressions7d: impr7,
        impressionsYesterday: imprY,
        impressionsToday: imprT,
        createdTime: `2026-0${(adNum % 6) + 1}-${String((adNum % 27) + 1).padStart(2, '0')}T09:${String(adNum % 60).padStart(2, '0')}:00+0300`,
      };

      if (g.dpa) {
        ad.products = VEHICLE_CLASSES.slice(0, 3).map((v, pi) => ({
          name: v.name,
          retailerId: v.sku,
          url: `https://www.velorent.com/en/fleet/${v.sku.toLowerCase()}`,
          image: img(`velorent-veh-${v.sku}-${pi}`, 400, 300),
        }));
      }
      if (broken) {
        ad._demoVerdict = { verdict: broken.verdict, reason: broken.reason };
      }
      ads.push(ad);
    }
  });

  const accounts = Object.values(M_ACCOUNTS).map((a) => ({
    accountId: a.accountId,
    accountName: a.accountName,
    count: ads.filter((x) => x.accountId === a.accountId).length,
  }));

  return { ads, total: ads.length, accounts };
}

const VEHICLE_CLASSES = [
  { name: 'Economy Hatchback — Fiat Egea Hatchback', sku: 'VR-ECOH' },
  { name: 'Compact SUV — Renault Duster', sku: 'VR-CSUV' },
  { name: 'Premium Sedan — BMW 3 Series', sku: 'VR-PSED' },
  { name: 'Minivan 7+1 — Ford Tourneo Custom', sku: 'VR-MVAN' },
  { name: 'Electric Compact — Togg T10X', sku: 'VR-EVCP' },
];

/* ------------------------------------------------------------------ *
 * AI consistency verdicts (no backend call for the demo brand)
 * ------------------------------------------------------------------ */

// Mirrors the /consistency-check response: [{ id, verdict, reason }].
// `makeId` is the page's adKey() so the verdicts map back onto the rows.
export function buildVeloRentVerdicts(ads, makeId) {
  return ads.map((a) => ({
    id: makeId(a),
    verdict: a._demoVerdict?.verdict || 'consistent',
    reason: a._demoVerdict?.reason || CONSISTENT(a.adGroupName || a.adsetName || 'ad group'),
  }));
}

export const VELORENT_EXPECTED_TOTALS = { google: GOOGLE_TOTAL, meta: META_TOTAL };
